import { useFeatureValue } from '@growthbook/growthbook-react';
import Box from '@mui/material/Box';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import Tooltip from '@mui/material/Tooltip';
import { Loader } from '@openx/components-core/lib/Loader/Loader.tsx';
import { PageNotFound } from '@openx/components-core/lib/PageNotFound/PageNotFound.tsx';
import { DealAmazonSyncTab } from '@openx/components-deal/lib/DealDetails/DealAmazonSyncTab.tsx';
import { DealDetailsTab } from '@openx/components-deal/lib/DealDetails/DealDetailsTab.tsx';
import { DealTddSyncTab } from '@openx/components-deal/lib/DealDetails/DealTddSyncTab.tsx';
import { DealTroubleshooting } from '@openx/components-deal/lib/DealDetails/DealTroubleshooting.tsx';
import { DetailsHeaderAlerts } from '@openx/components-deal/lib/DealDetails/DetailsHeaderAlerts.tsx';
import { useCurrentTab } from '@openx/components-deal/lib/DealDetails/useCurrentTab.ts';
import { useDealContext } from '@openx/components-deal/lib/utils/context/DealContext.tsx';
import { HeaderActions } from '@openx/components-header/HeaderActions/HeaderActions.tsx';
import { PageHeader } from '@openx/components-header/PageHeader.tsx';
import {
  ALLOWLIST_DEAL_PACING,
  ALLOWLIST_FREQUENCY_CAPPING,
  ENABLED_FREQUENCY_CAPPING,
} from '@openx/constants/feature-flag-keys.ts';
import { type DealFormDataWithTargeting, type DealOaApi, dealDetailsTabs } from '@openx/types/deal.ts';
import { Feature_Flags_Enum } from '@openx/types/gqlTypes.ts';
import { useEmailAllowlist } from '@openx/utils/hooks/useEmailAllowlist.ts';
import { usePermissionContext } from '@openx/utils/permission/context/PermissionContext.tsx';
import { useSnackbar } from 'notistack';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { type Location, Navigate, Route, Routes, useLocation, useNavigate, useParams } from 'react-router';
import { useAuth } from '../../components/AuthContext/context.ts';
import { GROWTHBOOK_SALT } from '../../config.ts';
import { GetDealDocument } from '../../graphql/query/deals/GetDeal.generated.tsx';
import { isAdmin, isInternalUser } from '../../permissions/validators.ts';
import { listSearchParams } from '../../utils/useListCriteria.ts';
import { useFeatureFlag } from '../featureFlags/useFeatureFlag.ts';
import { NestedErrorCode } from '../graphql/types.ts';
import { useLocalErrorHandling } from '../graphql/useLocalErrorHandling.ts';
import { DealHistoryTab } from './DealDetails/DealHistoryTab.tsx';
import { useHeaderActions } from './DealDetails/useHeaderActions.tsx';

type LocationState = {
  navigateFromGroup?: boolean;
  navigateFromDealInsights?: boolean;
  redirectPath?: string;
  deal?: DealFormDataWithTargeting;
};

export function DealDetailsPage() {
  const { session } = useAuth();
  const { activeTab, changeTab } = useCurrentTab();
  const { useDealFetch } = useDealContext();
  const hasCuratedDealBuyerDiscountFeature = useFeatureFlag(Feature_Flags_Enum.CuratedDealBuyerDiscount);
  const isFrequencyCappingEnabled = useFeatureValue(ENABLED_FREQUENCY_CAPPING, false);
  const allowListFrequencyCapping: string = useFeatureValue(ALLOWLIST_FREQUENCY_CAPPING, '');
  const location: Location<LocationState> = useLocation();
  const navigate = useNavigate();
  const { dealUid } = useParams();
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslation();

  const { loading: dealLoading, deal: fetchedDeal } = useDealFetch<DealOaApi>(dealUid ?? '');
  const [justDeleted, setJustDeleted] = useState(false);
  const deal = useMemo(
    () => (fetchedDeal && justDeleted ? { ...fetchedDeal, deleted: 1 } : fetchedDeal),
    [fetchedDeal, justDeleted]
  );
  const hasThirdPartyFeesConfig = !!deal?.third_party_fees_config?.[0];
  const isInternalAdmin = isAdmin(session) && isInternalUser(session);

  const { isGrowthbookFeatureOn } = usePermissionContext();
  const isPacingVisible = isGrowthbookFeatureOn(ALLOWLIST_DEAL_PACING);

  const { inAllowList: inAllowListFrequencyCapping } = useEmailAllowlist({
    email: session?.email,
    allowList: allowListFrequencyCapping,
    salt: GROWTHBOOK_SALT,
  });

  const isAllowFrequencyCapping =
    isFrequencyCappingEnabled &&
    (!allowListFrequencyCapping.length || (!!allowListFrequencyCapping.length && inAllowListFrequencyCapping));
  const tddSync = deal?.synchronized_with?.pdp_sync;
  const isOldTddSync = !!deal?.synchronized_with?.ttd_proposal_id;
  const shouldShowTtdSyncTab = Boolean(tddSync) && !isOldTddSync && deal?.old_ttd_sync_allowed === false;
  const amazonSync = deal?.synchronized_with?.amazon_sync;
  const shouldShowAmazonSyncTab = Boolean(amazonSync);
  const isDirectDeal = !!deal?.is_direct_deal;

  const handleChangeTab = useCallback(
    (event: React.SyntheticEvent, value: string) => {
      if (isDirectDeal && value !== dealDetailsTabs.DETAILS) {
        event.preventDefault();
        return;
      }

      if (!tddSync && value === dealDetailsTabs.TTD_SYNC) {
        event.preventDefault();
        return;
      }

      if (!amazonSync && value === dealDetailsTabs.AMAZON_SYNC) {
        event.preventDefault();
        return;
      }

      changeTab(event, value);
    },
    [amazonSync, changeTab, isDirectDeal, tddSync]
  );

  useEffect(() => {
    const requestedTab = location.pathname.split('/').pop();
    if (isDirectDeal && requestedTab && requestedTab !== dealUid && requestedTab !== dealDetailsTabs.DETAILS) {
      enqueueSnackbar(t('This tab is not available for direct deals.'), { variant: 'error' });
    }
  }, [isDirectDeal, location.pathname, dealUid, enqueueSnackbar, t]);

  const errorCallback = useCallback(
    error => {
      if (error.code === NestedErrorCode.VALIDATION_ERROR && error.message) {
        enqueueSnackbar(error.message, { variant: 'error' });
        return true;
      }
      return false;
    },
    [enqueueSnackbar]
  );

  useLocalErrorHandling(GetDealDocument, errorCallback);

  const { actions: headerActions } = useHeaderActions({
    deal,
    onDeleted: useCallback(result => {
      if (result?.fulfilledIds?.length) {
        setJustDeleted(true);
      }
    }, []),
  });

  const header = useCallback(() => <HeaderActions actions={headerActions} />, [headerActions]);

  const onGoBack = useCallback(() => {
    if (location.state?.redirectPath) {
      const { redirectPath, ...state } = location.state;
      navigate(redirectPath, { state });
      return;
    }

    if (location.state?.navigateFromGroup || location.state?.navigateFromDealInsights) {
      navigate(-1);
      return;
    }

    navigate(`/deals?${listSearchParams.deals ?? ''}`);
  }, [location.state, navigate]);

  if (dealLoading) {
    return <Loader />;
  }

  if (!deal || !dealUid) {
    return <PageNotFound appName={'OpenXSelect'} />;
  }

  const pageTitle = `${t('Details:')} ${deal.name}`;

  const isDetailsTab = activeTab === dealDetailsTabs.DETAILS;
  // direct deals only render the Details tab, so fall back to it while the route redirect kicks in
  const tabsValue = isDirectDeal && activeTab !== dealDetailsTabs.DETAILS ? dealDetailsTabs.DETAILS : activeTab;

  return (
    <PageHeader title={pageTitle} goBack={onGoBack} render={header} titlePrefix={'OpenXSelect'}>
      <Tabs onChange={handleChangeTab} value={tabsValue}>
        <Tab data-test="details-tab" label={t('Details')} value={dealDetailsTabs.DETAILS} />
        {!isDirectDeal && <Tab data-test="history-tab" value={dealDetailsTabs.HISTORY} label={t('History')} />}
        {!isDirectDeal && (
          <Tab
            data-test="ttd-sync-tab"
            value={dealDetailsTabs.TTD_SYNC}
            label={
              <Tooltip title={!shouldShowTtdSyncTab ? t('Sync not available') : ''} placement="top">
                <Box
                  component="span"
                  sx={{
                    display: 'inline-block',
                    pointerEvents: 'auto',
                    cursor: shouldShowTtdSyncTab ? 'pointer' : 'default',
                  }}
                >
                  {t('TTD PDP API Sync')}
                </Box>
              </Tooltip>
            }
            sx={{
              pointerEvents: shouldShowTtdSyncTab ? 'auto' : 'none',
              opacity: shouldShowTtdSyncTab ? 1 : 0.5,
              '&.MuiTab-root:hover': {
                backgroundColor: 'transparent',
              },
            }}
          />
        )}
        {!isDirectDeal && (
          <Tab
            data-test="amazon-sync-tab"
            value={dealDetailsTabs.AMAZON_SYNC}
            label={
              <Tooltip title={!shouldShowAmazonSyncTab ? t('Sync not available') : ''} placement="top">
                <Box
                  component="span"
                  sx={{
                    display: 'inline-block',
                    pointerEvents: 'auto',
                    cursor: shouldShowAmazonSyncTab ? 'pointer' : 'default',
                  }}
                >
                  {t('Amazon Deals API Sync')}
                </Box>
              </Tooltip>
            }
            sx={{
              pointerEvents: shouldShowAmazonSyncTab ? 'auto' : 'none',
              opacity: shouldShowAmazonSyncTab ? 1 : 0.5,
              '&.MuiTab-root:hover': {
                backgroundColor: 'transparent',
              },
            }}
          />
        )}
        {!isDirectDeal && (
          <Tab data-test="troubleshoot-tab" value={dealDetailsTabs.TROUBLESHOOT} label={t('Troubleshoot')} />
        )}
      </Tabs>

      {isDetailsTab && <DetailsHeaderAlerts deal={deal} />}

      <Routes>
        <Route
          path="details"
          element={
            <DealDetailsTab
              deal={{ ...deal, name: deal?.name ?? '' }}
              showDiscounts={hasCuratedDealBuyerDiscountFeature && hasThirdPartyFeesConfig}
              showPacing={isPacingVisible}
              showFrequencyCapping={isAllowFrequencyCapping}
              isAdmin={isInternalAdmin}
            />
          }
        />
        <Route
          path="history"
          element={
            isDirectDeal ? (
              <Navigate replace to={`../${dealDetailsTabs.DETAILS}`} />
            ) : (
              <DealHistoryTab dealId={deal.id} />
            )
          }
        />
        <Route
          path={dealDetailsTabs.TROUBLESHOOT}
          element={
            isDirectDeal ? (
              <Navigate replace to={`../${dealDetailsTabs.DETAILS}`} />
            ) : (
              <DealTroubleshooting dealId={deal.id} dealParticipants={deal.deal_participants} />
            )
          }
        />
        <Route
          path="ttd-sync"
          element={
            shouldShowTtdSyncTab && tddSync && !isDirectDeal ? (
              <DealTddSyncTab deal={{ synchronized_with: { pdp_sync: tddSync } }} />
            ) : (
              <Navigate replace to={`../${dealDetailsTabs.DETAILS}`} />
            )
          }
        />
        <Route
          path="amazon-sync"
          element={
            shouldShowAmazonSyncTab && amazonSync && !isDirectDeal ? (
              <DealAmazonSyncTab deal={{ synchronized_with: { amazon_sync: amazonSync } }} />
            ) : (
              <Navigate replace to={`../${dealDetailsTabs.DETAILS}`} />
            )
          }
        />
        <Route path="*" element={<Navigate to={`../${dealDetailsTabs.DETAILS}`} />} />
      </Routes>
    </PageHeader>
  );
}
